import apiClient from "./baseService";
import {Order, PatchOrderBodyType, PostOrderBodyType, PostOrderResponseType} from "../types/domain/order";
import {handleAxiosError} from "./axiosErrorHandler";
import {OrderStatus} from "../types/domain/orderStatus";
import {OrderFinalizeResponse} from "../types/orderFinalizeResponse";
import {OrderFlags} from "../types/domain/orderFlags";
import {downloadBlob} from "../utils/downloadUtils";
import {PaginatedResponse} from "../types/PaginatedResponse";

export interface GetOrdersResponse {
    data: Order[];
    meta: {totalRowCount: number};
}

export const getOrders = async (query: URLSearchParams): Promise<PaginatedResponse<Order>> => {
    return await apiClient
        .get<PaginatedResponse<Order>>(`/orders?${query.toString()}`)
        .then((response) => response.data)
        .catch(handleAxiosError);
};

export const getOrderById = async (companyId: string, orderId: string): Promise<Order> => {
    const order = await apiClient
        .get<Order>(`/companies/${companyId}/orders/${orderId}`)
        .then((response) => response.data)
        .catch(handleAxiosError);
    return order;
};
export interface OrderQueryParams {
    state?: string[];
}
export const getOrdersByCompany = async (companyId: string): Promise<Order[]> => {
    return await apiClient
        .get<Order[]>(`/companies/${companyId}/orders`)
        .then((response) => response.data)
        .catch(handleAxiosError);
};

export const getOrdersForUser = async (userId: string, query?: OrderQueryParams): Promise<Order[]> => {
    let httpQuery = {};
    if (query) {
        httpQuery = {
            params: query,
        };
    }
    return await apiClient
        .get<Order[]>(`/users/${userId}/orders`, httpQuery)
        .then((response) => response.data)
        .catch(handleAxiosError);
};

export const editOrder = async (companyId: string, id: string, putOrderBody: PatchOrderBodyType): Promise<Order> => {
    return await apiClient
        .patch<Order>(`/companies/${companyId}/orders/${id}`, putOrderBody)
        .then((response) => response.data)
        .catch(handleAxiosError);
};

export const finalizeOrder = async (companyId: string, id: string): Promise<OrderFinalizeResponse> => {
    return await apiClient
        .post<OrderFinalizeResponse>(`/companies/${companyId}/orders/${id}/finalize`)
        .then((response) => response.data)
        .catch(handleAxiosError);
};

export const createOrder = async (companyId: string, body: PostOrderBodyType): Promise<PostOrderResponseType> => {
    return await apiClient
        .post<PostOrderResponseType>(`/companies/${companyId}/orders`, body)
        .then((response) => response.data)
        .catch(handleAxiosError);
};

export const addFields = async (companyId: string, orderId: string, body: {fieldIds: string[]}): Promise<string> => {
    return await apiClient
        .post<string>(`/companies/${companyId}/orders/${orderId}/add-fields`, body)
        .then((response) => response.data)
        .catch(handleAxiosError);
};

export const changeStatus = async (companyId: string, orderId: string, newStatus: OrderStatus): Promise<string> => {
    const body = {state: newStatus};
    return await apiClient
        .post<string>(`/companies/${companyId}/orders/${orderId}/state`, body)
        .then((response) => response.data)
        .catch(handleAxiosError);
};
export const changeFlags = async (companyId: string, orderId: string, flags: OrderFlags[]): Promise<string> => {
    const body = {flags: flags};
    return await apiClient
        .put<string>(`/companies/${companyId}/orders/${orderId}/flags`, body)
        .then((response) => response.data)
        .catch(handleAxiosError);
};
export const deleteOrder = async (companyId: string, id: string) => {
    await apiClient.delete(`/companies/${companyId}/orders/${id}`).catch(handleAxiosError);
};
export const downloadShapeFile = async (companyId: string, orderId: string) => {
    return await apiClient
        .get(`/companies/${companyId}/orders/${orderId}/shapefile`, {responseType: "blob"})
        .then((response) => {
            const contentDisposition: string | undefined = (response.headers as Record<string, string>)[
                "content-disposition"
            ];
            let fileName = "shapefile.zip";
            if (contentDisposition) {
                const fileNameMatch: RegExpMatchArray | null = CONTENT_DISPOSITION_REGEX.exec(contentDisposition);
                if (fileNameMatch?.[1]) {
                    fileName = fileNameMatch[1];
                }
            }
            downloadBlob(response.data as Blob, fileName);
        })
        .catch(handleAxiosError);
};

const CONTENT_DISPOSITION_REGEX = /filename="?(.+)"?/;
